import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { WebAddresses } from '../app.config';
import { catchError, map, Observable, of, tap, throwError } from 'rxjs';
import {
  masterComponent,
  masterCountryCode,
  masterIssueType,
  masterpKeyword,
  masterSeverity,
  masterStatusGranulated,
  masterSupportLevel,
  masterTicketType,
} from '../interfaces/masterData';
import { openSnackBar } from '../helpers/genericFunctions';
import { ApiResponse } from '../interfaces/api-response';
import { MatSnackBar } from '@angular/material/snack-bar';
import { globalUser } from '../helpers/globalFields';
import { COUNTRYCODES } from '../mock-data/mock-countryCodes';
import { MasterPermission } from '../interfaces/master-permission';
import { MasterOrganization } from '../interfaces/master-organization';

/**
 * main service to interact with all master data that is stored in the database. for every master data set a new function implements fetching it
 */
@Injectable({
  providedIn: 'root',
})
export class MasterDataService {
  /**
   * constructor
   *
   * @param http The HttpClient that GET, POST, PUT, DELETE on the api
   * @param snackBar Overlay to display errors and success messages to end use
   */
  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
  ) {}

  /**
   * allows easy switching between production and development webserver addresses for api
   */
  private baseUrl = WebAddresses.tCCTicketServer;
  private authUrl = WebAddresses.TicketAuthorisation;

  /**
   * pKeywords are reflecting groups of tickets. Similar content of tickets are assigned to similar keywords. Used for analyzing frequent problems that arise
   * during the usage of Ottobock products and help gain insight in problematic structures for the end user
   *
   * @returns all available keywords
   */
  getMasterpKeywordList(): Observable<masterpKeyword[]> {
    return this.http
      .get<ApiResponse>(`${this.baseUrl}/api/data/masterpKeyword`, {
        headers: globalUser.getHeaders(),
      })
      .pipe(
        tap((response) => {
          if (response.status != 'success') {
            openSnackBar(response.message, 10000, this.snackBar);
          }
        }),
        map((response) => response.data ?? []),
        catchError((error) => {
          console.error('Error fetching masterpKeyword:', error);
          return throwError(() => new Error('Failed to fetch masterpKeywords'));
        }),
      );
  }

  /**
   * ticketTypes internally distinguish between pCC and tCC. pCC tickets are directly created by the end user. tCC tickets are tickets that are currently under investigation by
   * the support team
   *
   * @returns list of all ticketTypes (currently two!)
   */
  getMasterTicketTypeList(): Observable<masterTicketType[]> {
    return this.http
      .get<ApiResponse>(`${this.baseUrl}/api/data/mastertickettype/findall`, {
        headers: globalUser.getHeaders(),
      })
      .pipe(
        tap((response) => {
          if (response.status != 'success') {
            openSnackBar(response.message, 10000, this.snackBar);
          }
        }),
        map((response) => response.data ?? []),
        catchError((error) => {
          console.error('Error fetching mastertickettypes:', error);
          return throwError(
            () => new Error('Failed to fetch mastertickettypes'),
          );
        }),
      );
  }

  /**
   * supportlevels describe the severity of a ticket.
   *    1 : just created and easily resolved
   *    2 : needed some work but fixable by support
   *    3 : unfixable by support. Need supervision from Ottobock directly
   *
   * @returns list of supportlevels
   */
  getMasterSupportLevelList(): Observable<masterSupportLevel[]> {
    return this.http
      .get<ApiResponse>(`${this.baseUrl}/api/data/mastersupportlevel/findall`, {
        headers: globalUser.getHeaders(),
      })
      .pipe(
        tap((response) => {
          if (response.status != 'success') {
            openSnackBar(response.message, 10000, this.snackBar);
          }
        }),
        map((response) => response.data ?? []),
        catchError((error) => {
          console.error('Error fetching mastersupportlevels:', error);
          return throwError(
            () => new Error('Failed to fetch mastersupportlevels'),
          );
        }),
      );
  }

  /**
   * issueTypes are predefined types that a ticket can have. They define if a ticket is connected to a bug, feature request, e.g.
   *
   * @returns list of all issueTypes defined by Ottobock
   */
  getMasterIssueTypeList(): Observable<masterIssueType[]> {
    return this.http
      .get<ApiResponse>(`${this.baseUrl}/api/data/masterissuetype/findall`, {
        headers: globalUser.getHeaders(),
      })
      .pipe(
        tap((response) => {
          if (response.status != 'success') {
            openSnackBar(response.message, 10000, this.snackBar);
          }
        }),
        map((response) => response.data ?? []),
        catchError((error) => {
          console.error('Error fetching masterissuetype:', error);
          return throwError(() => new Error('Failed to fetch masterissuetype'));
        }),
      );
  }

  /**
   * components describe what part of the product didn't work (e.g. Software, Hardware, Server Endpoints).
   *
   * @returns list of all Components that Ottobock products can have
   */
  getMasterComponentList(): Observable<masterComponent[]> {
    return this.http
      .get<ApiResponse>(`${this.baseUrl}/api/data/mastercomponent/findall`, {
        headers: globalUser.getHeaders(),
      })
      .pipe(
        tap((response) => {
          if (response.status != 'success') {
            openSnackBar(response.message, 10000, this.snackBar);
          }
        }),
        map((response) => response.data ?? []),
        catchError((error) => {
          console.error('Error fetching mastercomponent:', error);
          return throwError(() => new Error('Failed to fetch mastercomponent'));
        }),
      );
  }

  /**
   * The granulated stati describe at what stage of WIP a ticket is (e.g. Fixed, Reproducable, Rejected, etc.).
   *
   * @returns list of all possible stati a ticket can have
   */
  getMasterStatusGranulatedList(): Observable<masterStatusGranulated[]> {
    return this.http
      .get<ApiResponse>(
        `${this.baseUrl}/api/data/masterstatusgranulated/findall`,
        {
          headers: globalUser.getHeaders(),
        },
      )
      .pipe(
        tap((response) => {
          if (response.status != 'success') {
            openSnackBar(response.message, 10000, this.snackBar);
          }
        }),
        map((response) => response.data ?? []),
        catchError((error) => {
          console.error('Error fetching masterstatusgranulated:', error);
          return throwError(
            () => new Error('Failed to fetch masterstatusgranulated'),
          );
        }),
      );
  }

  /**
   * Severity describes how bad a ticket creating problem is
   *
   * @returns list of all severities
   */
  getMasterSeverityList(): Observable<masterSeverity[]> {
    return this.http
      .get<ApiResponse>(`${this.baseUrl}/api/data/masterseverity/findall`, {
        headers: globalUser.getHeaders(),
      })
      .pipe(
        tap((response) => {
          if (response.status != 'success') {
            openSnackBar(response.message, 10000, this.snackBar);
          }
        }),
        map((response) => response.data ?? []),
        catchError((error) => {
          console.error('Error fetching pKeywords:', error);
          return throwError(() => new Error('Failed to fetch pKeywords'));
        }),
      );
  }

  getMasterCountrycodeList(): Observable<masterCountryCode[]> {
    return this.http
      .get<ApiResponse>(`${this.baseUrl}/api/data/mastercountrycode/findall`, {
        headers: globalUser.getHeaders(),
      })
      .pipe(
        tap((response) => {
          if (response.status != 'success') {
            openSnackBar(response.message, 10000, this.snackBar);
          }
        }),
        map((response) => response.data ?? []),
        catchError((error) => {
          console.error('Error fetching mastercountrycode:', error);
          return throwError(
            () => new Error('Failed to fetch mastercountrycode'),
          );
        }),
      );
  }

  getMasterCountrycodeMockList(): Observable<masterCountryCode[]> {
    return of(COUNTRYCODES);
  }
  /**
   * masterPermissions define the available permission types that can be assigned to users.
   * Used to dynamically render the permission grid columns in the admin page instead of
   * hardcoding permission keys in the frontend.
   *
   * @returns list of all active master permissions
   */
  getMasterPermissionList(): Observable<MasterPermission[]> {
    return this.http
      .get<ApiResponse>(`${this.authUrl}/api/data/masterpermission/findall`, {
        headers: globalUser.getHeaders(),
      })
      .pipe(
        tap((response) => {
          console.log(response);
          if (response.status != 'success') {
            openSnackBar(response.message, 10000, this.snackBar);
          }
        }),
        map((response) => response.data ?? []),
        catchError((error) => {
          console.error('Error fetching master permissions:', error);
          return throwError(
            () => new Error('Failed to fetch master permissions'),
          );
        }),
      );
  }

  /**
   * masterorganizations define the available organization types that can be assigned to users.
   * Used to dynamically render the organization grid columns in the admin page instead of
   * hardcoding organization keys in the frontend.
   *
   * @returns list of all active master organizations
   */
  getMasterOrganizationList(): Observable<MasterOrganization[]> {
    return this.http
      .get<ApiResponse>(`${this.authUrl}/api/data/masterorganization/findall`, {
        headers: globalUser.getHeaders(),
      })
      .pipe(
        tap((response) => {
          console.log(response);
          if (response.status != 'success') {
            openSnackBar(response.message, 10000, this.snackBar);
          }
        }),
        map((response) => response.data ?? []),
        catchError((error) => {
          console.error('Error fetching master organizations:', error);
          return throwError(
            () => new Error('Failed to fetch master organizations'),
          );
        }),
      );
  }
}
